import React, { useContext, useMemo, useState } from 'react';
import { EchartsWithTheme, Empty, Modal, useTheme } from '@maxtropy/components';
import styles from './index.module.scss';
import outerStyles from '../index.module.scss';
import { V2GasMonitorMapGasUsageTrendPostResponse } from '@maxtropy/device-customer-apis-v2';
import { COLORS, option } from './option';
import { Spin } from 'antd';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import { GasMonitorContext } from '../../../context';
import { formatThousandUnit } from '@/utils/utils';
import { IndexTimeActive } from '../../LeftBox/SwitchDate';
import { isEmpty } from 'lodash-es';

interface GasTrendModalProps {
  open: boolean;
  onClose: () => void;
}

export type GasTrendItem = Exclude<V2GasMonitorMapGasUsageTrendPostResponse['list'], undefined>[number];

const GasTrendModal: React.FC<GasTrendModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const { isFullScreen, scale } = useContext(ShareDataWrapperContext);
  const { timeResolution, gasUsageTrendData: data, gasUsageTrendLoading: loading } = useContext(GasMonitorContext);

  const [selectedMediumId, setSelectedMediumId] = useState<number | undefined>();

  const mediumList = useMemo(() => data?.list ?? [], [data]);

  const timeFormat = timeResolution === IndexTimeActive.THIS_MONTH ? 'MM-DD' : 'M月';

  const selectedMedium = useMemo(() => {
    if (mediumList.length === 0) return undefined;
    if (!selectedMediumId) return mediumList[0];
    return mediumList.find(m => m.mediumId === selectedMediumId) ?? mediumList[0];
  }, [selectedMediumId, mediumList]);

  const chartOption = useMemo(() => option(timeFormat, selectedMedium), [selectedMedium]);

  return (
    <Modal
      title="用气趋势"
      open={open}
      onCancel={onClose}
      footer={null}
      size="big"
      mask={false}
      destroyOnClose
      centered
      style={{ top: isFullScreen ? 80 * (scale ?? 1) : 110 * (scale ?? 1) }}
      className={outerStyles.customModal}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
    >
      <Spin spinning={loading}>
        <div className={styles.modalContent}>
          {isEmpty(mediumList) ? (
            <Empty style={{ marginTop: 200 }} />
          ) : (
            <>
              {/* 介质卡片列表 — 最多2行，超出滚动 */}
              <div className={styles.mediumTabs}>
                {mediumList.map((medium, idx) => {
                  const isSelected = selectedMedium?.mediumId === medium.mediumId;
                  return (
                    <div
                      key={medium.mediumId ?? idx}
                      className={`${styles.mediumCard} ${isSelected ? styles.cardActive : ''}`}
                      onClick={() => setSelectedMediumId(medium.mediumId)}
                    >
                      <div className={styles.cardTitle}>
                        {medium.mediumName ?? '--'} ({medium.physicalUnitName ?? '--'})
                      </div>
                      <div className={styles.cardUsage}>{formatThousandUnit(medium.gasUsage, 1)}</div>
                      <div className={styles.cardPlan}>
                        计划用量：
                        <strong style={{ color: '#FFCB47' }}>{formatThousandUnit(medium.gasPlanUsage, 1)}</strong>
                      </div>
                      <div className={styles.cardPlan}>
                        成本：
                        <span>{formatThousandUnit(medium.gasCost, 1)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* 选中气体的汇总信息 */}
              {selectedMedium?.isEnergyLedgerPeriod && (
                <div className={styles.summaryBar}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>
                      {selectedMedium.mediumName ?? '--'}-常规用能（{selectedMedium.physicalUnitName ?? '--'}）：
                    </span>
                    <span className={styles.summaryValue} style={{ color: COLORS[0] }}>
                      {formatThousandUnit(selectedMedium.regularUsage, 1)}
                    </span>
                    <span className={styles.summaryLabel}>成本：</span>
                    <span className={styles.summaryValue} style={{ color: '#FFF' }}>
                      {formatThousandUnit(selectedMedium.regularCost, 1)}
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>
                      {selectedMedium.mediumName ?? '--'}-延时用能（{selectedMedium.physicalUnitName ?? '--'}）：
                    </span>
                    <span className={styles.summaryValue} style={{ color: COLORS[1] }}>
                      {formatThousandUnit(selectedMedium.delayUsage, 1)}
                    </span>
                    <span className={styles.summaryLabel}>成本：</span>
                    <span className={styles.summaryValue} style={{ color: '#FFF' }}>
                      {formatThousandUnit(selectedMedium.delayCost, 1)}
                    </span>
                  </div>
                </div>
              )}
              <div style={{ flex: 1, height: 0 }}>
                <EchartsWithTheme option={chartOption} theme={theme} style={{ height: '100%', width: '100%' }} />
              </div>
            </>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default GasTrendModal;
